import React, { useEffect, useState, useRef, useCallback } from "react";
import "./review.css";
import img1 from "../../Assets/Reviews/1.webp";
import img2 from "../../Assets/Reviews/2.webp";
import img3 from "../../Assets/Reviews/3.webp";
import img4 from "../../Assets/Reviews/4.webp";

const REVIEWS = [
  {
    image: img1,
    title: "Divy & Sneha",
    event: "Engagement",
    date: "12 Aug 2024",
    stars: 5,
    review:
      "Our engagement shoot was magical! The photographer captured the love and joy perfectly. Every frame tells our story beautifully. Highly recommend for couples starting their journey.",
  },
  {
    image: img2,
    title: "Rahul & Anjali",
    event: "Pre-Wedding",
    date: "20 Oct 2024",
    stars: 5,
    review:
      "The pre-wedding photos were absolutely stunning. It set the perfect tone for our wedding day. Professional, creative, and so easy to work with — the whole team made us feel so comfortable.",
  },
  {
    image: img3,
    title: "Pratiksha",
    event: "Modelling & Wedding",
    date: "18 Jan 2024",
    stars: 5,
    review:
      "From modelling shoots to our wedding day, every single moment was beautifully documented. The quality and artistry are truly unmatched. I will cherish these memories forever.",
  },
  {
    image: img4,
    title: "Vikram & Meera",
    event: "Wedding",
    date: "15 Nov 2024",
    stars: 5,
    review:
      "Our wedding was captured with such elegance and emotion. The photos tell our story perfectly. Thank you Shree Photos & Films for making our day absolutely unforgettable!",
  },
];

const INTERVAL = 4000;

const Stars = ({ count }) => (
  <div className="rv-stars" aria-label={`${count} out of 5 stars`}>
    {[...Array(5)].map((_, i) => (
      <svg key={i} width="16" height="16" viewBox="0 0 24 24"
        fill={i < count ? "#C9A84C" : "none"}
        stroke={i < count ? "#C9A84C" : "rgba(201,168,76,0.3)"}
        strokeWidth="1.5">
        <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4z"/>
      </svg>
    ))}
  </div>
);

const Review = () => {
  const [index,   setIndex]   = useState(0);
  const [paused,  setPaused]  = useState(false);
  const [animate, setAnimate] = useState(false);
  const timerRef  = useRef(null);
  const touchRef  = useRef(null);

  const goTo = useCallback((i) => {
    setAnimate(false);
    requestAnimationFrame(() => {
      setIndex((i + REVIEWS.length) % REVIEWS.length);
      requestAnimationFrame(() => setAnimate(true));
    });
  }, []);

  const next = useCallback(() => goTo(index + 1), [index, goTo]);
  const prev = useCallback(() => goTo(index - 1), [index, goTo]);

  /* Auto-advance */
  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(next, INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [paused, next]);

  /* Animate in on mount */
  useEffect(() => { setAnimate(true); }, []);

  /* Touch swipe */
  const onTouchStart = (e) => { touchRef.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    if (touchRef.current === null) return;
    const d = touchRef.current - e.changedTouches[0].clientX;
    if (Math.abs(d) > 40) d > 0 ? next() : prev();
    touchRef.current = null;
  };

  /* Keyboard */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft')  prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  const current = REVIEWS[index];

  return (
    <section className="rv-section" id="reviews">

      {/* ── HEADER ── */}
      <div className="rv-header">
        <span className="rv-eyebrow">What Clients Say</span>
        <h2 className="rv-title">Testimonials</h2>
        <div className="rv-ornament">
          <span />
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#C9A84C">
            <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4z"/>
          </svg>
          <span />
        </div>
      </div>

      {/* ── SLIDER ── */}
      <div
        className="rv-slider"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        aria-label="Client reviews slider"
      >
        {/* Big decorative quote mark */}
        <div className="rv-bigquote" aria-hidden="true">"</div>

        <div className={`rv-card ${animate ? 'rv-card--in' : ''}`}>

          {/* ── LEFT: TEXT ── */}
          <div className="rv-text-col">

            <Stars count={current.stars} />

            <blockquote className="rv-quote">
              <span className="rv-quote-open">"</span>
              {current.review}
              <span className="rv-quote-close">"</span>
            </blockquote>

            <div className="rv-divider">
              <span />
              <svg width="10" height="10" viewBox="0 0 24 24" fill="#C9A84C">
                <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4z"/>
              </svg>
              <span />
            </div>

            <div className="rv-client-info">
              <div>
                <h3 className="rv-client-name">{current.title}</h3>
                <div className="rv-client-meta">
                  <span className="rv-event-tag">{current.event}</span>
                  <span className="rv-date">{current.date}</span>
                </div>
              </div>
            </div>

            {/* Navigation */}
            
            {/* Counter */}
            <p className="rv-counter">{String(index + 1).padStart(2,'0')} / {String(REVIEWS.length).padStart(2,'0')}</p>
          </div>

          {/* ── RIGHT: IMAGE ── */}
          <div className="rv-img-col">
            <div className="rv-img-frame">
              <img src={current.image} alt={current.title} className="rv-img" />
              {/* Gold corner accents */}
              <span className="rv-corner rv-corner--tl" />
              <span className="rv-corner rv-corner--tr" />
              <span className="rv-corner rv-corner--bl" />
              <span className="rv-corner rv-corner--br" />
            </div>

            {/* Floating rating badge */}
            <div className="rv-badge">
              <span className="rv-badge-star">★</span>
              <span className="rv-badge-num">5.0</span>
              <span className="rv-badge-label">Rated</span>
            </div>
          </div>

        </div>

        {/* Progress bar */}
        <div className="rv-progress">
          <div
            className="rv-progress-fill"
            key={`${index}-${paused}`}
            style={{ animationPlayState: paused ? 'paused' : 'running' }}
          />
        </div>
      </div>

      {/* ── AVATAR STRIP ── */}
      <div className="rv-avatar-strip">
        {REVIEWS.map((r, i) => (
          <button
            key={i}
            className={`rv-avatar ${i === index ? 'rv-avatar--active' : ''}`}
            onClick={() => goTo(i)}
            aria-label={`View review by ${r.title}`}
          >
            <img src={r.image} alt={r.title} />
          </button>
        ))}
      </div>

    </section>
  );
};

export default Review;